<script setup lang="ts">
  import useMarvelBreakPoint from '@/components/MarvelMachine/MarvelBreakPoint.vue'
  import { lazyLoadComponent } from '@/helpers/lazyLoadComponent'
  import { useGAEventStore } from '@/store/GAEventTracking'
  import { useLightBoxStore } from '@/store/LightBox'
  import { useMMStore } from '@/store/MarvelMachine'
  import { useUserStore } from '@/store/User'
  const GamePopup = lazyLoadComponent({
    comp: () => import('@/components/MarvelMachine/MarvelGamePopup.vue'),
  })
  const HelpMessagePopup = lazyLoadComponent({
    comp: () => import('@/components/MarvelMachine/HelpMessagePopup.vue'),
  })
  const mmStore = useMMStore()
  const mmBreakPoint = useMarvelBreakPoint()
  const userStore = useUserStore()
  const gaEvent = useGAEventStore()
  const lbStore = useLightBoxStore()
  const pageVisibility = ref(false)
  let userInfoLoadedOnLoad = false
  let noPlayPopupRevealedOnLoad = false
  let tradeBlockedPopupRevealedOnLoad = false
  const helpMessagePopupDefaultSetting = {
    items: [{ component: HelpMessagePopup }],
    option: { class: 'marvel-machine-helpMessage-lightbox' },
  }
  const gamePopupDefaultSetting = {
    items: [{ component: GamePopup }],
    option: { class: 'marvel-machine-game-lightbox' },
  }

  const durationalSpinReminderLocalStorage = {
    setLocalStorage: () => {
      localStorage.setItem('durationalSpinReminder', 'true')
    },
    getLocalStorage: () => {
      return localStorage.getItem('durationalSpinReminder')
    },
  }

  onMounted(() => {
    mmStore.init()
    mmBreakPoint.init()
    // delay for UI appearance
    pageVisibility.value = true
    setTimeout(() => {
      // delay for UI appearance
      const $marvelMainBtns = document.querySelector('.mavel-machine__main-btns')
      $marvelMainBtns?.classList.add('show')
    }, 1000)
    window.addEventListener('keyup', userGameReset)
    if (durationalSpinReminderLocalStorage.getLocalStorage() === 'true') {
      mmStore.user.seenDurationalSpinReminder = true
    }
  })

  onUnmounted(() => {
    mmBreakPoint.uninit()
    window.removeEventListener('keyup', userGameReset)
  })

  watchEffect(() => {
    const { isLoggedIn } = userStore
    const { user } = mmStore
    const { enableSpin } = mmStore.marvelMachine.event

    // Make sure to get userinfo when user is not in bannedCountry.
    if (user.isUserBannedCountry) {
      showBannedCountryPopup()
      return
    }
    if (isLoggedIn === false) {
      loginPopup()
      return
    }
    if (isLoggedIn) {
      if (!userInfoLoadedOnLoad) {
        mmStore.userInfo()
        userInfoLoadedOnLoad = true
      }
      if (!user.seenDurationalSpinReminder) {
        if (user.hasOldSpinBalance) {
          showDurationalSpinReminderOldMarvelBalancePopup()
        } else {
          showDurationalSpinReminderPopup()
        }
      }

      if (user.seenDurationalSpinReminder) {
        if (enableSpin === false) {
          if (!noPlayPopupRevealedOnLoad) {
            showMarvelMachineOffPopup()
          }
        }
        if (enableSpin === true) {
          if (user.isTradeBlocked && !tradeBlockedPopupRevealedOnLoad) {
            showTradeBlockedPopup()
          }
        }
      }
    }
  })

  function userGameReset({ code }: { code: string }) {
    if (code === 'Escape') {
      mmStore.userGameReset()
    }
  }

  function afterSeenDurationalSpinReminder() {
    mmStore.user.seenDurationalSpinReminder = true
    durationalSpinReminderLocalStorage.setLocalStorage()
  }

  function showDurationalSpinReminderPopup() {
    lbStore.openLightBox({
      ...helpMessagePopupDefaultSetting,
      componentProp: {
        content: 'durationalSpinReminder',
      },
      onUnmount: () => {
        afterSeenDurationalSpinReminder()
      },
    })
  }

  function showDurationalSpinReminderOldMarvelBalancePopup() {
    lbStore.openLightBox({
      ...helpMessagePopupDefaultSetting,
      componentProp: {
        content: 'durationalSpinReminderOldMarvelBalance',
      },
      onUnmount: () => {
        afterSeenDurationalSpinReminder()
      },
    })
  }

  function showMarvelMachineOffPopup() {
    showHelpMessagePopup('noPlay')
    noPlayPopupRevealedOnLoad = true
  }

  function showBannedCountryPopup() {
    showHelpMessagePopup('bannedCountry')
  }

  function loginPopup() {
    showHelpMessagePopup('pleaseLoginToPlayMm')
  }

  function showTradeBlockedPopup() {
    tradeBlockedPopupRevealedOnLoad = true
    showHelpMessagePopup('tradeBlocked')
  }

  function showHelpMessagePopup(content: string) {
    lbStore.openLightBox({
      ...helpMessagePopupDefaultSetting,
      componentProp: {
        content: content,
      },
    })
  }

  function tooltip() {
    lbStore.openLightBox({ ...gamePopupDefaultSetting, componentProp: { content: 'howToPlay' } })
  }
  function prizes() {
    gaEvent.pushActionEvent({
      interaction: 'button_click',
      sectionNumber: mmStore.game.resultOn ? 2 : 1,
      sectionName: mmStore.game.resultOn ? 'After Tap!' : 'Marvel Machine',
      sectionContent: `Prizes`,
    })
    lbStore.openLightBox({
      items: [{ component: GamePopup }],
      componentProp: { content: 'prizes' },
      option: { class: 'marvel-machine-game-lightbox' },
    })

    lbStore.openLightBox({ ...gamePopupDefaultSetting, componentProp: { content: 'prizes' } })
  }
  function myItems() {
    gaEvent.pushActionEvent({
      interaction: 'button_click',
      sectionNumber: mmStore.game.resultOn ? 2 : 1,
      sectionName: mmStore.game.resultOn ? 'After Tap!' : 'Marvel Machine',
      sectionContent: `My Items`,
    })
    if (mmStore.user.isUserBannedCountry) {
      return
    }
    if (!userStore.isLoggedIn) {
      window.nexon.sso.login()
      return
    }
    lbStore.openLightBox({ ...gamePopupDefaultSetting, componentProp: { content: 'myItems' } })
  }
</script>

<template>
  <Transition name="fade">
    <div
      v-if="pageVisibility"
      v-ga-section-view
      class="mavel-machine-main"
      data-section-name="Marvel Machine"
      data-section-number="1"
    >
      <MarvelMainRobot />
      <MarvelResult />
      <MarvelButton
        v-ga-event="{
          sectionContent: 'Question Mark',
        }"
        class="mavel-machine__tooltip"
        type="tooltip"
        @click="tooltip()"
      />
      <MarvelItemsAnimationToggle />
      <MarvelBanner />
      <MarvelLegalNotice class="marvel-machine__legal-notice" />
      <MarvelDeco />
      <div class="mavel-machine__main-btns">
        <MarvelButton
          class="mavel-machine__myitems"
          :class="{
            inactive: mmStore.isGameBlocked,
          }"
          type="myitems"
          @click="myItems()"
        />
        <MarvelMainButton />
        <MarvelButton class="mavel-machine__prizes" type="prizes" @click="prizes()" />
      </div>
    </div>
  </Transition>
</template>

<style lang="scss">
  :root {
    --marvel-accent: #4d1b3f;
    --marvel-desktopRatio: 1;
    @media #{$smallDesktopForMarvel} {
      --marvel-desktopRatio: 0.7;
    }
    @media #{$tabletForMarvel} {
      --marvel-desktopRatio: 0.6;
    }
  }
</style>

<style lang="scss" scoped>
  .before-load {
    background: red;
  }
  .mavel-machine-main {
    font-family: 'maplestory-light';
    position: relative;
    max-width: 2766px;
    padding: 34px 20px 0;
    height: calc(958px * var(--marvel-desktopRatio));
    background: url('@/assets/imgs/mavelmachine/bg-v2.jpg') grey no-repeat center
      calc(-112px * var(--marvel-desktopRatio)) / calc(2766px * var(--marvel-desktopRatio)) auto;
    margin: 0 auto;
    overflow: hidden;
    &.result-active {
      &:after {
        opacity: 1;
      }
    }
  }
  .mavel-machine__tooltip {
    position: absolute;
    left: 50%;
    top: calc(28px * var(--marvel-desktopRatio));
    transform: translateX(calc(237px * var(--marvel-desktopRatio)));
    z-index: 10;
  }

  .mavel-machine__main-btns {
    position: absolute;
    z-index: 10;
    left: 50%;
    bottom: 32px;
    transform: translate(-50%, 0%) scale(1.5);
    display: flex;
    gap: 30px;
    align-items: center;
    opacity: 0;
    transition: all 0.3s;
    &.show {
      opacity: 1;
      transform: translate(-50%, 0%) scale(1);

      .mavel-machine__myitems,
      .mavel-machine__prizes {
        transform: translateX(0);
      }
    }
  }
  .mavel-machine__myitems {
    transition: all 0.3s, transform 0.3s 0.3s;
    transform: translateX(130%);
  }
  .mavel-machine__prizes {
    transition: all 0.3s, transform 0.3s 0.3s;
    transform: translateX(-130%);
  }

  .marvel-machine__legal-notice {
    position: absolute;
    width: calc(355px * var(--marvel-desktopRatio));
    left: calc(24px * var(--marvel-desktopRatio));
    top: calc(145px * var(--marvel-desktopRatio));
    z-index: 10;
  }

  @media #{$tabletForMarvel} {
    .mavel-machine__tooltip {
      right: auto;
      left: 25.5px;
      transform: translateX(0);
    }
  }

  @media #{$mobileForMarvel} {
    .mavel-machine-main {
      height: 175vw;
      background: url('@/assets/imgs/mavelmachine/bg-mobile-extended.jpg') no-repeat center top / 138% auto;
      padding: 14vw 10vw 0;
    }
    .mavel-machine-logo {
      width: 57vw;
      height: 22vw;
    }
    .mavel-machine__tooltip {
      top: 2.8vw;
      right: 3.3vw;
      left: auto;
      transform: none;
    }
    .mavel-machine__main-btns {
      width: 100%;
      bottom: 21.5vw;
      flex-wrap: wrap;
      justify-content: center;
      gap: 4vw;
    }

    .marvel-machine__legal-notice {
      width: 95%;
      left: 50%;
      transform: translateX(-50%);
      top: auto;
      bottom: 5vw;
    }

    .mavel-machine__myitems {
      order: 2;
    }
    .mavel-machine__prizes {
      order: 3;
    }

    .mavel-machine__myitems,
    .mavel-machine__prizes {
      transform: translateX(0);
    }
  }
</style>
